import { NgClass } from '@angular/common';
import { Component, inject, input } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import * as fromLayout from '../../layout';
import * as fromAssistantIcon from '../assistants/assistants.icon.helper';
import * as fromShared from '../shared';
import { ChatActions } from '../store/chats.actions';
import { ChatsState } from '../store/chats.reducer';

@Component({
  selector: 'squadbox-conversation-header-desktop',
  imports: [
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseAssistantCustomIconDirective,
    fromSeriousSystem.UseAssistantIconDirective,
    fromSeriousSystem.IconButtonDirective,
    NgClass,
  ],
  template: `
    @let assistant = selectedAssistant();
    <div
      class="
        flex items-center pr-18 pl-4 laptop:pr-0
        h-20 typo-p3 bg-background
      "
      [class.opacity-0]="!showTitle()"
      [ngClass]="{
        'h-20 justify-center': viewMode() === 'conversation',
        'h-16 border-b border-neutral-200 justify-start pl-6':
          viewMode().includes('doc-viewer'),
        'bg-gradient-f-vertical': !(viewMode() === 'doc-viewer-conversation'),
        'transition-[opacity,translate] duration-500': animate(),
        '-translate-y-full opacity-0': !showTitle(),
        'starting:-translate-y-full starting:opacity-0 translate-y-0 opacity-100':
          animate() && showTitle(),
      }"
    >
      <div
        class="
            flex flex-row overflow-hidden items-center gap-2 [&>svg]:text-primary-500/50
            [&>img,&>svg]:size-5 [&>img,&>svg]:shrink-0
          "
        [ngClass]="{
          'gap-4': viewMode().includes('doc-viewer'),
        }"
      >
        @if (assistant) {
          @switch (fromAssistantIcon.getLogoType(assistant.logo)) {
            @case ('assistant') {
              <svg
                [sdUseAssistantIcon]="
                  fromAssistantIcon.getAssistantIcon(assistant.logo)
                "
              ></svg>
            }
            @case ('custom') {
              <svg
                [sdUseAssistantCustomIcon]="
                  fromAssistantIcon.getAssistantCustomIcon(assistant.logo)
                "
              ></svg>
            }
            @default {
              <img [src]="assistant.logo" alt="Assistant Logo" />
            }
          }

          <div
            class="flex max-w-[calc(100%-2em)]"
            [ngClass]="{
              'flex-col text-left': viewMode().includes('doc-viewer'),
              'flex-row gap-2': viewMode() === 'conversation',
            }"
          >
            <span class="typo-p3 font-semibold max-w-60 truncate">{{
              assistant.name
            }}</span>
            <span class="text-neutral-500 max-w-60 truncate">
              {{ selectedChatTitle() }}
            </span>
          </div>
        } @else {
          <span class="text-neutral-500 max-w-60 truncate">
            {{ selectedChatTitle() }}
          </span>
        }
      </div>
    </div>
    <!-- Assistant profile button -->
    @if (assistant) {
      <button
        class="absolute flex justify-center align-center right-4"
        [class.top-4]="viewMode() === 'conversation'"
        [class.top-2]="viewMode() !== 'conversation'"
        sdIconButton
        variant="icon"
        color="neutral"
        size="md"
        (click)="handleClickAssistantProfileCTA()"
      >
        <svg sdUseUIBasicIcon="folder-gear"></svg>
      </button>
    }
  `,
  styles: [
    `
      :host {
        @apply w-full z-10;
      }
    `,
  ],
})
export class ConversationHeaderDesktopResponsive {
  protected readonly fromAssistantIcon = fromAssistantIcon;
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<fromLayout.LayoutState>>(Store);

  public readonly selectedChatTitle = input.required<string>();
  public readonly selectedAssistant = input<Pick<
    fromGenerated.AssistantView,
    'name' | 'logo'
  > | null>();
  public readonly showTitle = input<boolean>(false);
  public readonly animate = input<boolean>(false);

  public readonly viewMode = this.layoutStore.selectSignal(
    fromLayout.layoutFeature.selectViewMode,
  );

  public handleClickAssistantProfileCTA() {
    if (this.viewMode() === 'conversation') {
      this.chatsStore.dispatch(ChatActions.toggleAssistantProfile());
    } else {
      this.chatsStore.dispatch(
        fromShared.DocumentViewerActions.toggleAssistantProfileAndConversationViewMode(),
      );
    }
  }
}
